/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  assertAccountExists,
  assertAccountsExist,
  combineCodec,
  decodeAccount,
  fetchEncodedAccount,
  fetchEncodedAccounts,
  fixDecoderSize,
  fixEncoderSize,
  getAddressDecoder,
  getAddressEncoder,
  getArrayDecoder,
  getArrayEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getStructDecoder,
  getStructEncoder,
  getU8Decoder,
  getU8Encoder,
  transformEncoder,
  type Account,
  type Address,
  type EncodedAccount,
  type FetchAccountConfig,
  type FetchAccountsConfig,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type MaybeAccount,
  type MaybeEncodedAccount,
  type ReadonlyUint8Array,
} from "@solana/kit";

export const TOKEN_BADGE_DISCRIMINATOR: ReadonlyUint8Array = new Uint8Array([
  116, 219, 204, 229, 249, 116, 255, 150,
]);

export function getTokenBadgeDiscriminatorBytes(): ReadonlyUint8Array {
  return fixEncoderSize(getBytesEncoder(), 8).encode(TOKEN_BADGE_DISCRIMINATOR);
}

export type TokenBadge = {
  discriminator: ReadonlyUint8Array;
  /** token mint */
  tokenMint: Address;
  /** Reserve */
  padding: Array<number>;
};

export type TokenBadgeArgs = {
  /** token mint */
  tokenMint: Address;
  /** Reserve */
  padding: Array<number>;
};

/** Gets the encoder for {@link TokenBadgeArgs} account data. */
export function getTokenBadgeEncoder(): FixedSizeEncoder<TokenBadgeArgs> {
  return transformEncoder(
    getStructEncoder([
      ["discriminator", fixEncoderSize(getBytesEncoder(), 8)],
      ["tokenMint", getAddressEncoder()],
      ["padding", getArrayEncoder(getU8Encoder(), { size: 128 })],
    ]),
    (value) => ({ ...value, discriminator: TOKEN_BADGE_DISCRIMINATOR }),
  );
}

/** Gets the decoder for {@link TokenBadge} account data. */
export function getTokenBadgeDecoder(): FixedSizeDecoder<TokenBadge> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
    ["tokenMint", getAddressDecoder()],
    ["padding", getArrayDecoder(getU8Decoder(), { size: 128 })],
  ]);
}

/** Gets the codec for {@link TokenBadge} account data. */
export function getTokenBadgeCodec(): FixedSizeCodec<
  TokenBadgeArgs,
  TokenBadge
> {
  return combineCodec(getTokenBadgeEncoder(), getTokenBadgeDecoder());
}

export function decodeTokenBadge<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress>,
): Account<TokenBadge, TAddress>;
export function decodeTokenBadge<TAddress extends string = string>(
  encodedAccount: MaybeEncodedAccount<TAddress>,
): MaybeAccount<TokenBadge, TAddress>;
export function decodeTokenBadge<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress> | MaybeEncodedAccount<TAddress>,
): Account<TokenBadge, TAddress> | MaybeAccount<TokenBadge, TAddress> {
  return decodeAccount(
    encodedAccount as MaybeEncodedAccount<TAddress>,
    getTokenBadgeDecoder(),
  );
}

export async function fetchTokenBadge<TAddress extends string = string>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<Account<TokenBadge, TAddress>> {
  const maybeAccount = await fetchMaybeTokenBadge(rpc, address, config);
  assertAccountExists(maybeAccount);
  return maybeAccount;
}

export async function fetchMaybeTokenBadge<TAddress extends string = string>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<MaybeAccount<TokenBadge, TAddress>> {
  const maybeAccount = await fetchEncodedAccount(rpc, address, config);
  return decodeTokenBadge(maybeAccount);
}

export async function fetchAllTokenBadge(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<Account<TokenBadge>[]> {
  const maybeAccounts = await fetchAllMaybeTokenBadge(rpc, addresses, config);
  assertAccountsExist(maybeAccounts);
  return maybeAccounts;
}

export async function fetchAllMaybeTokenBadge(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<MaybeAccount<TokenBadge>[]> {
  const maybeAccounts = await fetchEncodedAccounts(rpc, addresses, config);
  return maybeAccounts.map((maybeAccount) => decodeTokenBadge(maybeAccount));
}
